import * as R from 'ramda'
import {paymentPropriety} from '@kapitalist/contract/gameplay/payment'
import {MillionsMath} from '@kapitalist/contract/utils/millions/math'
import * as S from '@kapitalist/utils/Set'
import type {TSkin} from '~/game/skins'
import PropertyCard from '~/models/game/Cards/PropertyCard'
import type {PlayerGamePosition} from '~/models/game/GamePosition'
import type {
  AnyCard,
  AnyMinorActionPaySomething,
  AnyValuableCard,
} from '~/models/game/types'
import {combinations} from '~/utils/etc'

type TObviousPaymentResponseType =
  | 'exactValue' // they have a single cash card for exactly the charged amount. this is usually the right play.
  | 'wipedOut' // they owe everything they have.
  | 'onlyChoice' // there is only one legal way to make the payment.
  | 'lowestDenomination' // ~exactValue but overpaying. e.g. $2m rent, but all I have is $3m cash cards
interface IObviousPaymentResponseBase {
  type: TObviousPaymentResponseType
}
interface TObviousPaymentResponseExactValue extends IObviousPaymentResponseBase {
  type: 'exactValue'
  paidWith: AnyValuableCard[] // actually just one card, but simpler for now to always pass an array
}
interface TObviousPaymentResponseWipedOut extends IObviousPaymentResponseBase {
  type: 'wipedOut'
  paidWith: AnyValuableCard[]
}
interface TObviousPaymentResponseOnlyChoice extends IObviousPaymentResponseBase {
  type: 'onlyChoice'
  paidWith: AnyValuableCard[]
}
interface TObviousPaymentResponseLowestDenomination extends IObviousPaymentResponseBase {
  type: 'lowestDenomination'
  paidWith: AnyValuableCard[] // actually just one card, but simpler for now to always pass an array
}
type TObviousPaymentResponse =
  | TObviousPaymentResponseExactValue
  | TObviousPaymentResponseWipedOut
  | TObviousPaymentResponseOnlyChoice
  | TObviousPaymentResponseLowestDenomination

/**
 * given a scenario where the player owes money, there may be some obvious
 * payment we can present to the user as a shortcut.
 */
export const getObviousPayment = (
  payFor: AnyMinorActionPaySomething,
  playerGamePosition: PlayerGamePosition
): TObviousPaymentResponse | undefined => {
  const {currentPlayer} = playerGamePosition
  const {millionsOwed} = payFor
  const maxCapablePayment = currentPlayer.portfolio.totalValue()
  const allValuableCards = currentPlayer.portfolio.allValuableCards()
  const liquidatedCards = currentPlayer.portfolio.liquidatedCards
  const allValuableCardIds = S.create(allValuableCards.map(c => c.id))

  const isProperPayment = (selectedCardIds: Set<string>): boolean =>
    paymentPropriety(millionsOwed, allValuableCards, selectedCardIds).type ===
    'proper'

  if (maxCapablePayment <= millionsOwed) {
    // they owe everything they have.
    return {type: 'wipedOut', paidWith: allValuableCards}
  }

  // search the cash pile for obvious payments
  const exactValueCard = liquidatedCards.find(lc => lc.value === millionsOwed)
  if (exactValueCard) {
    return {type: 'exactValue', paidWith: [exactValueCard]}
  }

  // if paying with every card we have isProperPayment, then we are also wipedOut.
  // e.g. I have 2x$2m cards and the rent is $3m, even though I have more than $3m i have no choice.
  if (isProperPayment(allValuableCardIds)) {
    return {type: 'wipedOut', paidWith: allValuableCards}
  }

  const cardLessThanOrEqualToPayment = allValuableCards.find(
    lc => lc.value <= millionsOwed
  )
  if (!cardLessThanOrEqualToPayment) {
    // they have no cards valued less than the payment amount. If they have a cash card
    // with a value equal to their lowest value card (say, a $3m cash and a $3m property)
    // they likely just want to use the cash.
    const lowestCardValue = allValuableCards.reduce(
      (v, c) => MillionsMath.min(v, c.value),
      allValuableCards[0].value ?? 0
    )
    const cashCardWithLowestValue = liquidatedCards.find(
      c => c.value === lowestCardValue
    )
    if (cashCardWithLowestValue) {
      return {type: 'lowestDenomination', paidWith: [cashCardWithLowestValue]}
    }
  }

  if (allValuableCards.length < 5) {
    // for small hand counts, we can try something more complex.
    // if only one valid combination of cards can pay, then we can preselect it, too.
    // say, the rent is $3m and we have 2x$1m and a 2x$5m, we have to pay with a $5m.
    // limited to small card counts to limit performance effects (untested, could be fine higher?)
    // but theoretically this situation is also harder to come across with many cards
    const validCombos = combinations(allValuableCards).filter(
      cs => cs.length && isProperPayment(S.create(cs.map(c => c.id)))
    )

    // treat all non-properties as identical before checking if there is only one way to pay,
    // since all equal-value cash/residences/action cards/rents are identical once liquidated to the cash pile
    const stringifiedCombos = new Set<string>(
      validCombos.map(vc =>
        vc
          .map(c => (c instanceof PropertyCard ? c.id : c.value))
          .sort()
          .join(',')
      )
    )

    if (stringifiedCombos.size === 1) {
      return {type: 'onlyChoice', paidWith: validCombos[0]}
    }
  }

  return undefined
}

// converts ['Pass Go', 'Pass Go', '$5M'] to ['2x Pass Go', '$5M']
export const groupedCardDisplayNames = (cards: AnyCard[], skin: TSkin): string[] => {
  const cardNames = cards.map(c => c.shortDisplayName(skin))
  const dupesGrouped = R.groupWith(R.equals, cardNames)
  return dupesGrouped.map(grouping =>
    grouping.length > 1 ? `${grouping.length}x ${grouping[0]}` : grouping[0]
  )
}
